/* Sundays in Bed — shared cost-estimator engine.
   Single source of truth for pricing constants and maths.
   Used by: /pages/cost-calculator (page body), the Style Quiz, and the quote-email worker.
   Behaviour must match the original inline calculator exactly — change values here only. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.SIBEstimator = factory(); }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* Room model — stable IDs. FOC rule: yard/utility/foyer free when >=1 bedroom AND >=1 open-plan. */
  var ROOMS = { bed: 'Bedroom', liv: 'Living room', din: 'Dining room', kit: 'Kitchen', bath: 'Bathroom', off: 'Home office', bal: 'Balcony', opn: 'Open-plan (living–dining ± kitchen)', util: 'Utility / yard', foyer: 'Foyer', oth: 'Others' };

  /* Design fees confirmed by owner 2026-08-08: standard rooms / open-plan split. */
  var RATE = { s: { std: 1715, opn: 2635 }, d: { std: 3565, opn: 5485 } };

  /* Finish-level multipliers on the design fee (owner 2026-08-08): Essential x1, Premium x1.2, Signature x1.3. */
  var MULT = { e: 1, p: 1.2, s: 1.3 };

  /* Contractor-works bands. [low,high]; paint/floor/ffe are per sq ft, others lump sums.
     TODO OWNER (F09): works bands pending — placeholders, not confirmed. */
  var WORKS = {
    e: { name: 'Essential', paint: [4, 7],  floor: [9, 16],  kit: [8000, 15000],  ward: [6000, 12000],  elec: [3000, 6000],   plumb: [2000, 5000],  ffe: [8, 15] },
    p: { name: 'Premium',   paint: [7, 12], floor: [16, 32], kit: [15000, 30000], ward: [12000, 24000], elec: [6000, 12000],  plumb: [4000, 9000],  ffe: [15, 30] },
    s: { name: 'Signature', paint: [12, 22], floor: [32, 65], kit: [30000, 60000], ward: [24000, 45000], elec: [12000, 25000], plumb: [8000, 18000], ffe: [30, 60] }
  };
  var PER_SQFT_WORKS = { paint: true, floor: true, ffe: true };

  var LIMITS = { minSqft: 450, maxSqft: 10000, minRoomCount: 0, maxRoomCount: 9 };

  var DEFAULTS = {
    counts: { bed: 2, liv: 1, din: 1, kit: 0, bath: 0, off: 0, bal: 0, opn: 0, util: 0, foyer: 0, oth: 0 },
    svc: 'd',
    tier: 'p',
    sqft: 1000,
    works: ['paint', 'kit', 'ward']
  };

  function r100(n) { return Math.round(n / 100) * 100; }
  function clampSqft(sq) {
    sq = parseInt(sq, 10);
    if (isNaN(sq)) sq = DEFAULTS.sqft;
    return Math.min(LIMITS.maxSqft, Math.max(LIMITS.minSqft, sq));
  }
  function normCounts(counts) {
    var out = {};
    for (var k in ROOMS) {
      var v = counts && counts[k] != null ? parseInt(counts[k], 10) : 0;
      if (isNaN(v)) v = 0;
      out[k] = Math.max(LIMITS.minRoomCount, Math.min(LIMITS.maxRoomCount, v));
    }
    return out;
  }

  /* state: {counts:{bed..}, svc:'s'|'d', tier:'e'|'p'|'s', sqft:Number, works:['paint',...]} */
  function estimate(state) {
    state = state || {};
    var counts = normCounts(state.counts || DEFAULTS.counts);
    var svc = (state.svc === 's' || state.svc === 'd') ? state.svc : DEFAULTS.svc;
    var tier = WORKS[state.tier] ? state.tier : DEFAULTS.tier;
    var sq = clampSqft(state.sqft != null ? state.sqft : DEFAULTS.sqft);
    var works = Array.isArray(state.works) ? state.works : DEFAULTS.works.slice();

    var roomsN = 0, k;
    for (k in counts) roomsN += counts[k];

    /* FOC: utility/yard AND foyer are each free when the project has >=1 bedroom + >=1 open-plan
       (previously one combined "yard/utility/foyer" slot; now two separate room picks, each
       free under the same qualifying condition — confirmed with owner 2026-08-17). */
    var focActive = counts.bed >= 1 && counts.opn >= 1;
    var freeN = focActive ? (counts.util + counts.foyer) : 0;
    var chargeN = roomsN - freeN;

    /* Design fee is flat per room and never scales with finish tier — tier (Essential/
       Premium/Signature) only affects the contractor-works estimate below, never the fee
       itself. This matches the site's own "one flat fee per room" positioning; fixed 2026-08-17
       after finish tier was found silently multiplying the design fee too. */
    var rate = RATE[svc];
    var rStd = rate.std, rOpn = rate.opn;
    var opnN = counts.opn, stdN = chargeN - opnN;
    var fee = stdN * rStd + opnN * rOpn;

    var t = WORKS[tier], lo = 0, hi = 0, any = false;
    for (var i = 0; i < works.length; i++) {
      var w = works[i];
      if (!t[w] || w === 'name') continue;
      any = true;
      if (PER_SQFT_WORKS[w]) { lo += t[w][0] * sq; hi += t[w][1] * sq; }
      else { lo += t[w][0]; hi += t[w][1]; }
    }
    lo = r100(lo); hi = r100(hi);

    return {
      roomsN: roomsN, chargeN: chargeN, freeN: freeN, stdN: stdN, opnN: opnN,
      focActive: focActive, rStd: rStd, rOpn: rOpn, fee: fee,
      anyWorks: any, worksLo: lo, worksHi: hi,
      totalLo: any ? r100(fee + lo) : fee,
      totalHi: any ? r100(fee + hi) : fee,
      tierName: t.name, svc: svc, tier: tier, sqft: sq,
      counts: counts, works: works.slice()
    };
  }

  /* Quiz size bands — stable keys. Email ranges run the engine at band bounds (clamped). */
  var SIZE_BANDS = {
    b1: { label: '1–1,000 sq ft',          lo: 1,    hi: 1000 },
    b2: { label: '1,001–2,000 sq ft',      lo: 1001, hi: 2000 },
    b3: { label: '2,001–3,000 sq ft',      lo: 2001, hi: 3000 },
    b4: { label: 'More than 3,001 sq ft',  lo: 3001, hi: LIMITS.maxSqft }
  };

  function bandBounds(key) {
    var b = SIZE_BANDS[key];
    if (!b) b = SIZE_BANDS.b2;
    return { lo: clampSqft(b.lo), hi: clampSqft(b.hi) };
  }
  function bandRepresentative(key) {
    var b = bandBounds(key);
    var mid = (b.lo + b.hi) / 2;
    return clampSqft(Math.round(mid / 50) * 50);
  }
  /* Range across a size band for a given state (fee is size-invariant; works scale). */
  function estimateBand(state, bandKey) {
    var b = bandBounds(bandKey);
    var loState = {}, hiState = {}, k;
    for (k in state) { loState[k] = state[k]; hiState[k] = state[k]; }
    loState.sqft = b.lo; hiState.sqft = b.hi;
    var eLo = estimate(loState), eHi = estimate(hiState);
    return { lo: eLo, hi: eHi, bounds: b };
  }

  function fmtRM(n) { return 'RM ' + Number(n).toLocaleString('en-MY'); }

  return {
    ROOMS: ROOMS, RATE: RATE, MULT: MULT, WORKS: WORKS, LIMITS: LIMITS, DEFAULTS: DEFAULTS,
    SIZE_BANDS: SIZE_BANDS,
    estimate: estimate, estimateBand: estimateBand,
    bandBounds: bandBounds, bandRepresentative: bandRepresentative,
    clampSqft: clampSqft, r100: r100, fmtRM: fmtRM
  };
}));
